import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { myToaster } from '@interstellar-component'
import { AuditTrailService } from './service'

const AuditTrailContext = createContext()

// FE column key → BE sortable field. Whitelisted server-side in
// backoffice-service/modules/audit_trail/audit_trail.repositories.js
// (VALID_SORT_COLUMNS).
const SORT_FIELD_TO_API = {
  timestamp: 'timestamp',
  event: 'event_name',
  event_name: 'event_name',
  event_code: 'event_code',
  module: 'module',
  user: 'actor.name',
  outcome: 'outcome',
  platform: 'platform',
  created_at: 'created_at',
}

function AuditTrailProvider({ children }) {
  const [auditTrail, setAuditTrail] = useState({
    data: [],
    meta: {},
    filter: [],
    loading: false,
  })

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    search: '',
    filter: [],
    module: null,
    event_code: null,
    actor_id: null,
    outcome: null,
    platform: null,
    date_from: null,
    date_to: null,
    sort: null,
    order: null,
  })

  const getAuditTrail = useCallback(async () => {
    const limit = params.limit ?? 10
    const sortApi = params.sort ? SORT_FIELD_TO_API[params.sort] ?? params.sort : null
    const query = {
      page: params.page,
      limit,
      ...(params.search ? { search: params.search } : {}),
      ...(Array.isArray(params.filter) && params.filter.length > 0 ? { filter: params.filter } : {}),
      ...(params.module ? { module: params.module } : {}),
      ...(params.event_code ? { event_code: params.event_code } : {}),
      ...(params.actor_id ? { actor_id: params.actor_id } : {}),
      ...(params.outcome ? { outcome: params.outcome } : {}),
      ...(params.platform ? { platform: params.platform } : {}),
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(sortApi ? { sort: sortApi } : {}),
      ...(params.order ? { order: params.order } : {}),
    }
    setAuditTrail((s) => ({ ...s, loading: true }))
    try {
      const res = await AuditTrailService.getAuditTrail(query)
      const rows = Array.isArray(res?.data) ? res.data : []
      const rawMeta = res?.meta
      const perPageForMeta = Number(rawMeta?.per_page ?? rawMeta?.limit ?? limit) || limit
      const meta =
        rawMeta && typeof rawMeta === 'object' && !Array.isArray(rawMeta)
          ? {
              ...rawMeta,
              total: Number(rawMeta.total ?? rows.length),
              total_page:
                rawMeta.total_page ??
                rawMeta.last_page ??
                Math.max(1, Math.ceil(Number(rawMeta.total ?? rows.length) / perPageForMeta)),
            }
          : {
              current_page: 1,
              per_page: limit,
              total: rows.length,
              total_page: Math.max(1, Math.ceil(rows.length / perPageForMeta)),
            }
      setAuditTrail({
        data: rows,
        meta,
        filter: res?.filter ?? [],
        loading: false,
      })
    } catch (e) {
      myToaster(e)
      setAuditTrail((s) => ({ ...s, loading: false }))
    }
  }, [params])

  useEffect(() => {
    getAuditTrail()
  }, [getAuditTrail])

  const setPage = useCallback((page) => {
    setParams((p) => ({ ...p, page }))
  }, [])

  const setSearchTerm = useCallback((search) => {
    setParams((p) => ({ ...p, page: 1, search }))
  }, [])

  const handleSort = useCallback(({ sort, order }) => {
    setParams((p) => ({ ...p, page: 1, sort: sort ?? null, order: order ?? null }))
  }, [])

  const setFilter = useCallback((patch) => {
    setParams((p) => ({ ...p, page: 1, ...patch }))
  }, [])

  const exportXlsx = useCallback(() => {
    const url = AuditTrailService.exportAuditTrail({
      search: params.search,
      module: params.module,
      event_code: params.event_code,
      actor_id: params.actor_id,
      outcome: params.outcome,
      platform: params.platform,
      date_from: params.date_from,
      date_to: params.date_to,
      filter: params.filter,
      sort: params.sort ? SORT_FIELD_TO_API[params.sort] ?? params.sort : null,
      order: params.order,
    })
    window.open(url, '_blank')
  }, [params])

  const contextValue = useMemo(
    () => ({
      params,
      setParams,
      auditTrail,
      setPage,
      setSearchTerm,
      handleSort,
      setFilter,
      exportXlsx,
      sortField: params.sort,
      sortOrder: params.order,
    }),
    [params, auditTrail, setPage, setSearchTerm, handleSort, setFilter, exportXlsx]
  )

  return <AuditTrailContext.Provider value={contextValue}>{children}</AuditTrailContext.Provider>
}

const useAuditTrail = () => {
  const context = useContext(AuditTrailContext)
  if (context === undefined) {
    throw new Error('useAuditTrail must be used within an AuditTrailProvider')
  }
  return context
}

export { AuditTrailProvider, useAuditTrail }
